import { useMemo, useState, type FormEvent } from 'react';
import { Button, Field, Input } from '@busbook/ui';
import { api } from '../../lib/api.js';
import { useCustomer } from '../../lib/customer-session.js';
import { useTravellers } from '../../lib/travellers.js';
import { useFeature } from '../../lib/tenant.js';
import { useT } from '../../i18n/index.js';
import type { BookingView } from '../Ticket.js';

/**
 * Sending a ticket, and choosing who gets it.
 *
 * A guest sees the button they always saw: one tap, straight to the address on the booking,
 * because that is the only address the server will accept from them. Naming a recipient is
 * offered to the signed-in owner alone — see the route for why holding a magic link is not
 * the same as owning a booking.
 */

type Send = 'idle' | 'sending' | 'sent' | 'failed';

interface Recipient {
  email: string;
  /** What this address is, shown beside it — "your account", a traveller's name. */
  note: string;
}

export function EmailTicketAction({
  booking,
  label,
}: {
  booking: BookingView;
  label: string;
}) {
  const t = useT();
  const { user } = useCustomer();

  const [open, setOpen] = useState(false);
  const [state, setState] = useState<Send>('idle');
  const [sentTo, setSentTo] = useState('');

  // Only the owner may name a recipient, so only the owner is offered the choice. Everyone
  // else gets the original one-tap button rather than a picker whose options would all be
  // refused.
  const canChoose = Boolean(user);

  async function send(recipientEmail?: string) {
    setState('sending');
    try {
      const result = await api.request<{ sent: boolean; to: string }>(
        `/public/bookings/${booking.reference}/email-ticket`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          // Omitted entirely when it is the booking's own address: the server treats an
          // absent recipient as "the usual one", which is the path a guest is allowed.
          body: JSON.stringify(recipientEmail ? { recipientEmail } : {}),
        },
      );
      setSentTo(result.to);
      setState('sent');
      setOpen(false);
    } catch {
      setState('failed');
    }
  }

  if (state === 'sent') {
    return (
      <span className="trip-action-note" style={{ color: 'var(--signal-departed, inherit)' }}>
        {sentTo ? t('m.sentTo', { to: sentTo }) : t('m.ticketSent')}
      </span>
    );
  }

  if (!canChoose) {
    return (
      <>
        <button
          type="button"
          className="trip-action"
          onClick={() => void send()}
          disabled={state === 'sending'}
        >
          {state === 'sending' ? t('m.sending') : label}
        </button>
        {state === 'failed' && <span className="trip-action-note">{t('m.ticketSendFailed')}</span>}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="trip-action"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {label}
      </button>
      {state === 'failed' && <span className="trip-action-note">{t('m.ticketSendFailed')}</span>}
      {open && (
        <RecipientPicker
          contactEmail={booking.contactEmail}
          accountEmail={user?.email ?? null}
          busy={state === 'sending'}
          onCancel={() => setOpen(false)}
          onSend={(email) => void send(sameAddress(email, booking.contactEmail) ? undefined : email)}
        />
      )}
    </>
  );
}

/**
 * Mounted only once a passenger opens the picker, which is where the traveller list is
 * fetched. Loading it with the page would mean one identical request per upcoming trip on
 * screen, for a list most passengers will never open.
 */
function RecipientPicker({
  contactEmail,
  accountEmail,
  busy,
  onSend,
  onCancel,
}: {
  contactEmail: string;
  accountEmail: string | null;
  busy: boolean;
  onSend: (email: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const travellersEnabled = useFeature('travellers');
  const { travellers } = useTravellers(travellersEnabled);

  const known = useMemo<Recipient[]>(() => {
    const list: Recipient[] = [{ email: contactEmail, note: t('m.recipientBooking') }];
    if (accountEmail && !sameAddress(accountEmail, contactEmail)) {
      list.push({ email: accountEmail, note: t('m.recipientAccount') });
    }
    for (const traveller of travellers) {
      if (!traveller.email) continue;
      if (list.some((r) => sameAddress(r.email, traveller.email!))) continue;
      list.push({ email: traveller.email, note: traveller.fullName });
    }
    return list;
  }, [contactEmail, accountEmail, travellers, t]);

  // `''` is the sentinel for "someone else", which cannot collide with a real address.
  const [choice, setChoice] = useState(contactEmail);
  const [other, setOther] = useState('');
  const [error, setError] = useState<string | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    const email = choice === '' ? other.trim() : choice;
    if (!email) {
      setError(t('m.recipientNeeded'));
      return;
    }
    setError(null);
    onSend(email);
  }

  return (
    <form
      onSubmit={submit}
      // Full width and last in the flex row: the picker wraps onto its own line beneath
      // every action rather than shouldering the buttons that follow it apart.
      className="order-last mt-2 w-full rounded-lg border p-3"
      style={{ borderColor: 'var(--color-line)', background: 'var(--color-bg)' }}
    >
      <fieldset>
        <legend className="text-xs font-semibold">{t('m.sendTo')}</legend>
        <div className="mt-2 flex flex-col gap-1.5">
          {known.map((recipient) => (
            <label key={recipient.email} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="recipient"
                checked={choice === recipient.email}
                onChange={() => setChoice(recipient.email)}
              />
              <span className="min-w-0 truncate">{recipient.email}</span>
              <span className="shrink-0 text-xs" style={{ color: 'var(--color-muted)' }}>
                {recipient.note}
              </span>
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="recipient"
              checked={choice === ''}
              onChange={() => setChoice('')}
            />
            {t('m.recipientSomeoneElse')}
          </label>
        </div>
      </fieldset>

      {choice === '' && (
        <div className="mt-2">
          <Field label={t('m.recipientEmail')} htmlFor="recipient-other">
            <Input
              id="recipient-other"
              type="email"
              autoComplete="email"
              value={other}
              onChange={(e) => setOther(e.target.value)}
            />
          </Field>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm font-medium" style={{ color: 'var(--signal-cancelled)' }}>
          {error}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <Button type="submit" variant="secondary" loading={busy}>
          {t('m.send')}
        </Button>
        <button type="button" className="trip-action" onClick={onCancel}>
          {t('m.cancel')}
        </button>
      </div>
    </form>
  );
}

/** Addresses are compared case-insensitively; the server does the same before forwarding. */
function sameAddress(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
